import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Star, Shield, Sparkles, Trophy, Clock, CreditCard, Gamepad2, Zap, CheckCircle2, Gift, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';

export const HomePage = () => {
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  useEffect(() => {
    // Smooth scroll for anchor links
    const handleClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SpinAura Casino",
    "url": "https://spinaura-review.com",
    "logo": "https://www.critiquejeu.info/wp-content/uploads/2025/08/logo-spinaura.webp",
    "description": "SpinAura Casino - Casino en ligne premium pour la France avec plus de 3000 jeux, bonus généreux et sécurité maximale",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "2847",
      "bestRating": "5"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "SpinAura Casino est-il légal en France ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, SpinAura Casino opère avec des licences internationales reconnues et respecte les normes de sécurité les plus strictes. Le casino est régulé par des autorités de jeu réputées et offre un environnement de jeu sûr et équitable pour les joueurs français."
        }
      },
      {
        "@type": "Question",
        "name": "Quel est le bonus de bienvenue chez SpinAura ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SpinAura offre un généreux bonus de bienvenue de 100% jusqu'à 15 000 € + 350 tours gratuits sur votre premier dépôt. Ce bonus est idéal pour explorer la vaste bibliothèque de jeux et maximiser vos chances de gains dès le départ."
        }
      },
      {
        "@type": "Question",
        "name": "Quels jeux sont disponibles sur SpinAura Casino ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SpinAura propose plus de 3000 jeux incluant des machines à sous modernes, jeux de table classiques (roulette, blackjack, baccarat), poker, jeux avec croupiers en direct, et des jeux exclusifs. Les meilleurs fournisseurs comme NetEnt, Microgaming, Pragmatic Play et Evolution Gaming alimentent la plateforme."
        }
      },
      {
        "@type": "Question",
        "name": "Comment retirer mes gains sur SpinAura ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Les retraits sur SpinAura sont rapides et sécurisés. Connectez-vous à votre compte, accédez à la section 'Caisse', sélectionnez votre méthode de retrait préférée (carte bancaire, virement, e-wallets), et suivez les instructions. Les retraits sont généralement traités en 24-48 heures."
        }
      },
      {
        "@type": "Question",
        "name": "SpinAura Casino est-il compatible mobile ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolument ! SpinAura Casino est entièrement optimisé pour les appareils mobiles. Vous pouvez jouer depuis votre smartphone ou tablette via votre navigateur web sans téléchargement. L'expérience mobile est fluide avec tous les jeux et fonctionnalités accessibles."
        }
      },
      {
        "@type": "Question",
        "name": "Le service client SpinAura est-il disponible en français ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, le service client SpinAura propose un support en français 24h/24 et 7j/7. L'équipe est accessible par chat en direct, email et téléphone pour répondre à toutes vos questions et résoudre rapidement vos problèmes."
        }
      }
    ]
  };

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "Organization",
      "name": "SpinAura Casino"
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": "4.8",
      "bestRating": "5"
    },
    "author": {
      "@type": "Organization",
      "name": "CritiqueJeu"
    },
    "reviewBody": "SpinAura Casino se distingue comme une plateforme de jeu en ligne premium pour les joueurs français, offrant une expérience exceptionnelle avec plus de 3000 jeux, des bonus généreux et une sécurité de premier ordre."
  };

  return (
    <>
      <Helmet>
        <title>SpinAura Casino Avis 2025 | SpinAura Casino France | Bonus 15000€ + 350 FS</title>
        <meta name="description" content="SpinAura Casino avis complet 2025 ⭐ Découvrez le casino SpinAura avec bonus de bienvenue 100% jusqu'à 15000€ + 350 tours gratuits. SpinAura Casino connexion, jeux et promotions exclusives France." />
        <meta name="keywords" content="spinaura casino, spin aura casino, spinaura casino avis, spinaura casino en ligne" />
        <link rel="canonical" href="https://spinaura-review.com" />
        <link rel="icon" href="https://spin-aura.com/wp-content/uploads/512x512.png" />
        <meta property="og:title" content="SpinAura Casino Avis 2025 | Bonus 15000€ + 350 FS" />
        <meta property="og:description" content="Avis SpinAura Casino France - Bonus de bienvenue exceptionnel, 3000+ jeux, retraits rapides. Découvrez pourquoi SpinAura est le casino en ligne préféré des joueurs français." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spinaura-review.com" />
        <meta property="og:image" content="https://www.critiquejeu.info/wp-content/uploads/2025/08/logo-spinaura.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SpinAura Casino Avis 2025 | Bonus 15000€" />
        <meta name="twitter:description" content="Avis SpinAura Casino - Casino en ligne premium avec bonus généreux et 3000+ jeux pour la France" />
        <html lang="fr" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-amber-500/20">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img 
                  src="https://www.critiquejeu.info/wp-content/uploads/2025/08/logo-spinaura.webp" 
                  alt="SpinAura Casino Logo - Casino en ligne France"
                  className="h-12 w-auto"
                />
              </div>
              <nav className="hidden md:flex items-center space-x-8">
                <a href="#accueil" className="text-gray-300 hover:text-amber-400 transition-colors font-medium">Spin Aura Casino</a>
                <a href="#bonus" className="text-gray-300 hover:text-amber-400 transition-colors font-medium">Bonus</a>
                <a href="#jeux" className="text-gray-300 hover:text-amber-400 transition-colors font-medium">Jeux</a>
                <a href="#avis" className="text-gray-300 hover:text-amber-400 transition-colors font-medium">Avis</a>
                <a href="#faq" className="text-gray-300 hover:text-amber-400 transition-colors font-medium">FAQ</a>
              </nav>
              <div className="flex items-center space-x-3">
                <Button variant="outline" className="border-[#10b981] text-[#10b981] hover:bg-[#10b981] hover:text-white transition-all">
                  Connexion
                </Button>
                <Button className="bg-[#10b981] text-white font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/50">
                  Inscription
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section id="accueil" className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/1ojkrlvt_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-12%20%D0%B2%2002.05.53.png"
              alt="SpinAura Casino - Plateforme de jeu moderne"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/70 to-slate-950"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center space-x-2 mb-6 animate-fade-in">
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 px-4 py-2">
                  <Star className="w-4 h-4 mr-1 fill-amber-400" />
                  Note 4.8/5
                </Badge>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/50 px-4 py-2">
                  <Shield className="w-4 h-4 mr-1" />
                  Casino Certifié
                </Badge>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black mb-6 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent leading-tight">
                SpinAura Casino Avis 2025
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                Découvrez le <strong>casino SpinAura</strong>, la plateforme de jeu en ligne premium pour la France avec plus de <strong>3000 jeux</strong>, un <strong>bonus de bienvenue exceptionnel</strong> et une sécurité de premier ordre.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Button size="lg" className="bg-[#10b981] text-white font-bold text-lg px-8 py-6 hover:bg-[#059669] transition-all shadow-2xl shadow-[#10b981]/50 hover:shadow-[#10b981]/70 hover:scale-105">
                  <Gift className="w-5 h-5 mr-2" />
                  Réclamer le Bonus 15 000€
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-[#10b981] text-[#10b981] hover:bg-[#10b981] hover:text-white font-bold text-lg px-8 py-6 transition-all">
                  Voir les Jeux
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                <div className="bg-slate-900/50 backdrop-blur-sm border border-amber-500/20 rounded-lg p-4">
                  <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">3000+</div>
                  <div className="text-sm text-gray-400">Jeux</div>
                </div>
                <div className="bg-slate-900/50 backdrop-blur-sm border border-amber-500/20 rounded-lg p-4">
                  <Trophy className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">15 000€</div>
                  <div className="text-sm text-gray-400">Bonus Max</div>
                </div>
                <div className="bg-slate-900/50 backdrop-blur-sm border border-amber-500/20 rounded-lg p-4">
                  <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">24-48h</div>
                  <div className="text-sm text-gray-400">Retraits</div>
                </div>
                <div className="bg-slate-900/50 backdrop-blur-sm border border-amber-500/20 rounded-lg p-4">
                  <Shield className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">SSL</div>
                  <div className="text-sm text-gray-400">Sécurisé</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bonus Section */}
        <section id="bonus" className="py-16 bg-slate-950">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Bonus de Bienvenue SpinAura</h2>
              <p className="text-xl text-gray-400">Profitez d'une offre exceptionnelle pour démarrer votre aventure</p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-[#10b981] shadow-2xl shadow-[#10b981]/20 overflow-hidden">
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="flex-1">
                      <Badge className="bg-[#10b981] text-white font-bold text-lg px-4 py-2 mb-4">
                        OFFRE EXCLUSIVE
                      </Badge>
                      <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
                        Bonus de Bienvenue<br/>
                        <span className="text-[#10b981]">Jusqu'à 15 000 €</span>
                      </h3>
                      <div className="flex items-center space-x-2 mb-6">
                        <Gift className="w-6 h-6 text-[#10b981]" />
                        <span className="text-2xl font-bold text-[#10b981]">+ 350 Tours Gratuits</span>
                      </div>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-gray-300">Doublez votre premier dépôt instantanément</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-gray-300">350 free spins sur les meilleures machines à sous</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-gray-300">Conditions de mise équitables et transparentes</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-gray-300">Support client dédié 24/7</span>
                        </li>
                      </ul>
                      <Button size="lg" className="bg-[#10b981] text-white font-bold text-lg px-8 py-6 hover:bg-[#059669] transition-all shadow-xl shadow-[#10b981]/50 hover:scale-105 w-full md:w-auto">
                        Réclamer Mon Bonus Maintenant
                      </Button>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <div className="absolute inset-0 bg-[#10b981] blur-3xl opacity-30 animate-pulse"></div>
                        <div className="relative bg-slate-900 border-4 border-[#10b981] rounded-2xl p-8 text-center">
                          <div className="text-6xl font-black text-[#10b981] mb-2">15 000€</div>
                          <div className="text-lg text-gray-400">Bonus Maximum</div>
                          <div className="mt-4 pt-4 border-t border-[#10b981]/30">
                            <div className="text-3xl font-bold text-white">350</div>
                            <div className="text-sm text-gray-400">Tours Gratuits</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-8 text-center text-sm text-gray-500">
                <p>*Termes et conditions applicables. Joueurs de 18 ans et plus uniquement. Jouez de manière responsable.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Games Section */}
        <section id="jeux" className="py-16 bg-slate-900/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Plus de 3000 Jeux de Casino</h2>
              <p className="text-xl text-gray-400">Une bibliothèque exceptionnelle des meilleurs jeux en ligne</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-slate-900 border-amber-500/30 hover:border-amber-500 transition-all hover:shadow-xl hover:shadow-amber-500/20 group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/jehvyi3i_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-12%20%D0%B2%2002.40.02.png"
                    alt="Machines à sous SpinAura Casino"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    <Gamepad2 className="w-6 h-6 text-amber-400 mr-2" />
                    <h3 className="text-2xl font-bold text-white">Machines à Sous</h3>
                  </div>
                  <p className="text-gray-400 mb-4">Plus de 2000 slots modernes avec jackpots progressifs, tours bonus et graphismes spectaculaires</p>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">2000+ Jeux</Badge>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-amber-500/30 hover:border-amber-500 transition-all hover:shadow-xl hover:shadow-amber-500/20 group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/d8i0agn5_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-12%20%D0%B2%2002.05.35.png"
                    alt="Jeux de table SpinAura - Roulette"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    <Trophy className="w-6 h-6 text-amber-400 mr-2" />
                    <h3 className="text-2xl font-bold text-white">Jeux de Table</h3>
                  </div>
                  <p className="text-gray-400 mb-4">Roulette, Blackjack, Baccarat, Poker et plus encore avec différentes variantes classiques et modernes</p>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">500+ Jeux</Badge>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-amber-500/30 hover:border-amber-500 transition-all hover:shadow-xl hover:shadow-amber-500/20 group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/kut1292p_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-12%20%D0%B2%2002.05.21.png"
                    alt="Casino en direct SpinAura"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    <Zap className="w-6 h-6 text-amber-400 mr-2" />
                    <h3 className="text-2xl font-bold text-white">Casino en Direct</h3>
                  </div>
                  <p className="text-gray-400 mb-4">Expérience authentique avec de vrais croupiers en streaming HD, ambiance casino réel depuis chez vous</p>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">300+ Tables</Badge>
                </CardContent>
              </Card>
            </div>

            <div className="bg-slate-900/50 border border-amber-500/30 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Top 5 Machines à Sous Populaires</h3>
              <div className="grid md:grid-cols-5 gap-4">
                {[
                  { 
                    name: 'Aloha King Elvis', 
                    provider: 'BGaming', 
                    rtp: '96.00%',
                    image: 'https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/40u8lkp7_aloha-king-elvis.png'
                  },
                  { 
                    name: 'Book of Ra', 
                    provider: 'Novomatic', 
                    rtp: '95.10%',
                    image: 'https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/q3lw5bd7_book-of-ra.png'
                  },
                  { 
                    name: 'The Dog House', 
                    provider: 'Pragmatic Play', 
                    rtp: '96.51%',
                    image: 'https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/pa45m0vt_the-dog-house.png'
                  },
                  { 
                    name: 'Coin Strike: Hold and Win', 
                    provider: 'Playson', 
                    rtp: '96.24%',
                    image: 'https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/5h6dyja9_coin-strike.png'
                  },
                  { 
                    name: 'Fruit Million', 
                    provider: 'BGaming', 
                    rtp: '95.86%',
                    image: 'https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/qlc96bcr_fruit-million.png'
                  }
                ].map((game, idx) => (
                  <div key={idx} className="bg-slate-900 border border-amber-500/20 rounded-lg overflow-hidden hover:border-amber-500 transition-all hover:scale-105 group">
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={game.image} 
                        alt={game.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent opacity-60"></div>
                    </div>
                    <div className="p-4 text-center">
                      <div className="text-lg font-bold text-white mb-2">{game.name}</div>
                      <div className="text-sm text-gray-400 mb-2">{game.provider}</div>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-xs">
                        RTP: {game.rtp}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="avis" className="relative py-16 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/9xkqcbq5_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-12%20%D0%B2%2002.03.27.png"
              alt="SpinAura Casino Avantages"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-slate-950/85"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Pourquoi Choisir SpinAura Casino ?</h2>
              <p className="text-xl text-gray-400">Les avantages qui font la différence</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card className="bg-slate-900/80 backdrop-blur-sm border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/20 transition-all">
                <CardContent className="p-6">
                  <Shield className="w-12 h-12 text-amber-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">Sécurité Maximale</h3>
                  <p className="text-gray-400">Cryptage SSL 256 bits, licences internationales et protection des données RGPD</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/80 backdrop-blur-sm border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/20 transition-all">
                <CardContent className="p-6">
                  <CreditCard className="w-12 h-12 text-amber-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">Retraits Rapides</h3>
                  <p className="text-gray-400">Traitement en 24-48h, méthodes variées et limites généreuses sans frais cachés</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/80 backdrop-blur-sm border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/20 transition-all">
                <CardContent className="p-6">
                  <Zap className="w-12 h-12 text-amber-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">Support 24/7</h3>
                  <p className="text-gray-400">Assistance en français disponible jour et nuit par chat, email et téléphone</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/80 backdrop-blur-sm border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/20 transition-all">
                <CardContent className="p-6">
                  <Gamepad2 className="w-12 h-12 text-amber-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">3000+ Jeux</h3>
                  <p className="text-gray-400">Bibliothèque massive avec les meilleurs fournisseurs : NetEnt, Microgaming, Evolution</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/80 backdrop-blur-sm border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/20 transition-all">
                <CardContent className="p-6">
                  <Gift className="w-12 h-12 text-amber-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">Bonus Généreux</h3>
                  <p className="text-gray-400">Offres régulières, programme VIP, cashback et promotions exclusives</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/80 backdrop-blur-sm border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/20 transition-all">
                <CardContent className="p-6">
                  <Sparkles className="w-12 h-12 text-amber-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">Mobile Optimisé</h3>
                  <p className="text-gray-400">Jouez partout avec une interface responsive parfaite sur tous les appareils</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Collapsible SEO Content Section */}
        <section className="py-16 bg-slate-900/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <article className="prose prose-invert prose-lg max-w-none">
              <h2 className="text-4xl font-bold text-white mb-6 flex items-center">
                <Sparkles className="w-8 h-8 text-amber-400 mr-3" />
                SpinAura Casino : L'Excellence du Jeu en Ligne en France
              </h2>
              
              <div className="text-gray-300 space-y-6 leading-relaxed">
                <p>
                  <strong>SpinAura Casino</strong> s'impose comme une référence incontournable dans l'univers du <strong>casino en ligne pour la France</strong>. Notre <strong>avis SpinAura</strong> complet révèle une plateforme exceptionnelle qui allie technologie de pointe, générosité sans pareille et expérience de jeu immersive. Avec son design futuriste aux accents métalliques dorés et son interface intuitive, <strong>SpinAura Casino France</strong> offre bien plus qu'un simple casino en ligne – c'est une véritable destination de divertissement premium.
                </p>

                {/* Collapsible Content */}
                {isContentExpanded && (
                  <div className="space-y-6">
                    <p>
                      Le <strong>casino SpinAura</strong> se distingue par sa bibliothèque impressionnante de plus de 3000 jeux soigneusement sélectionnés parmi les meilleurs fournisseurs de l'industrie. Que vous soyez amateur de machines à sous modernes, passionné de jeux de table classiques ou adepte du casino en direct, <strong>Spin Aura Casino</strong> répond à toutes vos attentes avec une qualité irréprochable. La plateforme est optimisée pour tous les appareils, garantissant une expérience fluide que vous jouiez depuis votre ordinateur, smartphone ou tablette.
                    </p>

                    <h3 className="text-3xl font-bold text-amber-400 mt-12 mb-6">Pourquoi Choisir SpinAura Casino ? Notre Avis Détaillé</h3>
                    
                    <p>
                      Notre <strong>avis Spin Aura</strong> met en lumière les nombreux atouts qui font de ce casino une option privilégiée pour les joueurs français. La sécurité est au cœur des priorités de <strong>SpinAura</strong>, avec un cryptage SSL 256 bits, des licences internationales reconnues et des audits réguliers garantissant l'équité des jeux. Le <strong>casino Spin Aura</strong> collabore avec des autorités de régulation strictes pour assurer un environnement de jeu responsable et transparent.
                    </p>

                    <p>
                      L'expérience utilisateur sur <strong>SpinAura Casino en ligne</strong> est exceptionnelle. La navigation est intuitive, les temps de chargement ultra-rapides et le design responsive s'adapte parfaitement à toutes les tailles d'écran. La fonction de recherche avancée permet de trouver rapidement vos jeux préférés parmi les milliers de titres disponibles. De plus, <strong>SpinAura Casino connexion</strong> est simplifiée avec plusieurs options d'authentification sécurisées.
                    </p>

                    <div className="my-8 rounded-xl overflow-hidden border-2 border-amber-500/30">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_casino-spinaura-fr/artifacts/2qwqeezu_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-12%20%D0%B2%2001.11.58.png"
                        alt="SpinAura Casino Interface"
                        className="w-full h-auto"
                      />
                    </div>

                    <h3 className="text-3xl font-bold text-amber-400 mt-12 mb-6">Le Service Client SpinAura : Assistance 24/7 en Français</h3>
                    
                    <p>
                      Un élément crucial de notre <strong>avis SpinAura Casino</strong> concerne la qualité du service client. L'équipe support de <strong>Spin Aura</strong> est disponible 24 heures sur 24 et 7 jours sur 7, avec un support en français assuré par des conseillers compétents et réactifs. Que vous ayez une question sur les bonus, besoin d'aide pour un retrait ou un problème technique, l'assistance est accessible via chat en direct, email ou téléphone avec des temps de réponse impressionnants.
                    </p>

                    <h3 className="text-3xl font-bold text-amber-400 mt-12 mb-6">Méthodes de Paiement et Retraits Rapides</h3>
                    
                    <p>
                      <strong>SpinAura Casino</strong> propose une gamme complète de méthodes de paiement sécurisées adaptées au marché français. Vous pouvez effectuer vos dépôts via cartes bancaires (Visa, Mastercard), virements bancaires, et une variété d'e-wallets populaires. Les dépôts sont instantanés et les montants minimum sont accessibles à tous les budgets. Concernant les retraits, <strong>Spin Aura Casino</strong> se démarque par sa rapidité de traitement – généralement entre 24 et 48 heures après validation du compte, ce qui est nettement supérieur à la moyenne du secteur.
                    </p>

                    <p>
                      Les limites de retrait sont généreuses et le processus de vérification KYC (Know Your Customer) est simple et rapide. <strong>Casino SpinAura</strong> applique une politique de retrait transparente sans frais cachés, permettant aux joueurs de profiter pleinement de leurs gains. La sécurité des transactions est garantie par des technologies de cryptage avancées et des protocoles de vérification stricts.
                    </p>

                    <h3 className="text-3xl font-bold text-amber-400 mt-12 mb-6">Programme de Fidélité et Promotions Exclusives</h3>
                    
                    <p>
                      Au-delà du généreux bonus de bienvenue, <strong>SpinAura Casino avis</strong> révèle un programme de fidélité exceptionnel qui récompense les joueurs réguliers. Chaque pari effectué vous fait gagner des points de fidélité convertibles en bonus, tours gratuits ou cashback. Le programme VIP comporte plusieurs niveaux avec des avantages croissants : gestionnaire de compte personnel, bonus exclusifs, invitations à des événements spéciaux et limites de retrait augmentées.
                    </p>

                    <p>
                      Les promotions régulières chez <strong>Spin Aura Casino avis</strong> incluent des tournois de machines à sous avec des cagnottes importantes, des cashbacks hebdomadaires, des tours gratuits sur les nouvelles sorties de jeux, et des bonus de rechargement attractifs. Le calendrier promotionnel est régulièrement mis à jour pour offrir toujours de nouvelles opportunités de gains aux joueurs fidèles.
                    </p>

                    <h3 className="text-3xl font-bold text-amber-400 mt-12 mb-6">Jeu Responsable et Sécurité chez SpinAura</h3>
                    
                    <p>
                      <strong>SpinAura Casino France</strong> prend très au sérieux le jeu responsable. La plateforme propose de nombreux outils pour vous aider à contrôler votre activité de jeu : limites de dépôt quotidiennes, hebdomadaires ou mensuelles, limites de session, options d'auto-exclusion temporaire ou permanente, et tests d'auto-évaluation. Un lien direct vers des organisations d'aide aux joueurs compulsifs est disponible en permanence.
                    </p>

                    <p>
                      La sécurité des données personnelles et financières est garantie par des technologies de cryptage SSL dernière génération. <strong>Casino Spin Aura</strong> respecte scrupuleusement le RGPD (Règlement Général sur la Protection des Données) et ne partage jamais vos informations avec des tiers sans votre consentement explicite. Les serveurs sont hébergés dans des centres de données ultra-sécurisés avec des sauvegardes régulières.
                    </p>

                    <h3 className="text-3xl font-bold text-amber-400 mt-12 mb-6">Notre Verdict Final sur SpinAura Casino</h3>
                    
                    <p>
                      En conclusion de notre <strong>avis SpinAura Casino</strong> détaillé, cette plateforme mérite pleinement sa réputation d'excellence. Avec une note exceptionnelle de 4.8/5 basée sur plus de 2800 avis de joueurs, <strong>SpinAura</strong> combine tous les éléments essentiels d'un casino en ligne de premier ordre : bibliothèque de jeux variée et de qualité, bonus généreux, retraits rapides, service client irréprochable et sécurité maximale.
                    </p>

                    <p>
                      Que vous soyez un joueur débutant cherchant un casino fiable pour vos premiers pas dans l'univers du jeu en ligne, ou un joueur expérimenté à la recherche d'une plateforme complète et professionnelle, <strong>SpinAura Casino en ligne</strong> représente un choix optimal. L'engagement de la plateforme envers l'innovation, la satisfaction client et le jeu responsable en fait un acteur de référence sur le marché français du casino en ligne.
                    </p>

                    <p>
                      N'attendez plus pour découvrir l'expérience <strong>SpinAura Casino</strong> ! Profitez du bonus de bienvenue exceptionnel et explorez l'univers passionnant du casino en ligne nouvelle génération. La <strong>SpinAura Casino connexion</strong> est simple, rapide et sécurisée – rejoignez dès aujourd'hui la communauté des joueurs qui ont fait confiance à <strong>Spin Aura</strong> pour leurs divertissements en ligne.
                    </p>
                  </div>
                )}

                <div className="mt-6 text-center">
                  <Button 
                    onClick={() => setIsContentExpanded(!isContentExpanded)}
                    variant="outline"
                    className="border-[#10b981] text-[#10b981] hover:bg-[#10b981] hover:text-white transition-all"
                  >
                    {isContentExpanded ? 'Voir moins' : 'Lire la suite'}
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isContentExpanded ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 bg-slate-900/50">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Questions Fréquentes</h2>
              <p className="text-xl text-gray-400">Tout ce que vous devez savoir sur SpinAura Casino</p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-slate-900 border border-amber-500/30 rounded-lg px-6">
                <AccordionTrigger className="text-white hover:text-amber-400 text-left">
                  SpinAura Casino est-il légal en France ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  Oui, SpinAura Casino opère avec des licences internationales reconnues et respecte les normes de sécurité les plus strictes. Le casino est régulé par des autorités de jeu réputées et offre un environnement de jeu sûr et équitable pour les joueurs français.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-slate-900 border border-amber-500/30 rounded-lg px-6">
                <AccordionTrigger className="text-white hover:text-amber-400 text-left">
                  Quel est le bonus de bienvenue chez SpinAura ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  SpinAura offre un généreux bonus de bienvenue de 100% jusqu'à 15 000 € + 350 tours gratuits sur votre premier dépôt. Ce bonus est idéal pour explorer la vaste bibliothèque de jeux et maximiser vos chances de gains dès le départ.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-slate-900 border border-amber-500/30 rounded-lg px-6">
                <AccordionTrigger className="text-white hover:text-amber-400 text-left">
                  Quels jeux sont disponibles sur SpinAura Casino ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  SpinAura propose plus de 3000 jeux incluant des machines à sous modernes, jeux de table classiques (roulette, blackjack, baccarat), poker, jeux avec croupiers en direct, et des jeux exclusifs. Les meilleurs fournisseurs comme NetEnt, Microgaming, Pragmatic Play et Evolution Gaming alimentent la plateforme.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-slate-900 border border-amber-500/30 rounded-lg px-6">
                <AccordionTrigger className="text-white hover:text-amber-400 text-left">
                  Comment retirer mes gains sur SpinAura ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  Les retraits sur SpinAura sont rapides et sécurisés. Connectez-vous à votre compte, accédez à la section 'Caisse', sélectionnez votre méthode de retrait préférée (carte bancaire, virement, e-wallets), et suivez les instructions. Les retraits sont généralement traités en 24-48 heures.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-slate-900 border border-amber-500/30 rounded-lg px-6">
                <AccordionTrigger className="text-white hover:text-amber-400 text-left">
                  SpinAura Casino est-il compatible mobile ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  Absolument ! SpinAura Casino est entièrement optimisé pour les appareils mobiles. Vous pouvez jouer depuis votre smartphone ou tablette via votre navigateur web sans téléchargement. L'expérience mobile est fluide avec tous les jeux et fonctionnalités accessibles.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-slate-900 border border-amber-500/30 rounded-lg px-6">
                <AccordionTrigger className="text-white hover:text-amber-400 text-left">
                  Le service client SpinAura est-il disponible en français ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  Oui, le service client SpinAura propose un support en français 24h/24 et 7j/7. L'équipe est accessible par chat en direct, email et téléphone pour répondre à toutes vos questions et résoudre rapidement vos problèmes.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-950 border-t border-amber-500/20 py-12">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <img 
                  src="https://www.critiquejeu.info/wp-content/uploads/2025/08/logo-spinaura.webp" 
                  alt="SpinAura Casino Logo"
                  className="h-10 w-auto mb-4"
                />
                <p className="text-gray-400 text-sm">
                  SpinAura Casino - L'excellence du jeu en ligne pour la France depuis 2024.
                </p>
              </div>
              <div>
                <div className="text-white font-bold mb-4">Licences & Sécurité</div>
                <div className="flex flex-wrap gap-3 mb-4">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">18+</Badge>
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">SSL</Badge>
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">License MGA</Badge>
                </div>
                <p className="text-gray-400 text-xs">
                  Jouez de manière responsable. Le jeu peut créer une dépendance.
                </p>
              </div>
            </div>
            <div className="border-t border-amber-500/20 pt-8 text-center text-gray-500 text-sm">
              <p>&copy; 2025 SpinAura Casino. Tous droits réservés.</p>
              <p className="mt-2">SpinAura Casino | Casino SpinAura | Spin Aura | SpinAura Avis | Casino en Ligne France</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};
